import React from 'react';
import { Modal, Input, FormPanel, SubmitButton } from '@ixicare/ui';
import { Device, DeviceEntryProps, DeviceType } from '@ixicare/resources';
import { useMutation } from '@tanstack/react-query';
import { t } from '~/i18n';
import styles from './DeviceModal.module.css';

export interface ArchivedDeviceInfo {
  deviceType: DeviceType;
  macAddress?: string;
  imeiNumber?: string;
  siteId: number;
}

interface DeviceCreateModalProps {
  archivedDevice: ArchivedDeviceInfo;
  onClose: () => void;
  onCreated: (device: DeviceEntryProps) => void;
}

export const DeviceCreateModal: React.FC<DeviceCreateModalProps> = ({ archivedDevice, onClose, onCreated }) => {
  const typeTranslation = t(`device.types.${archivedDevice.deviceType.toLowerCase()}`);
  const [description, setDescription] = React.useState(typeTranslation);

  const createMutation = useMutation({
    mutationFn: () =>
      Device.submitDevice({
        deviceType: archivedDevice.deviceType,
        macAddress: archivedDevice.macAddress,
        imeiNumber: archivedDevice.imeiNumber,
        description,
        siteId: archivedDevice.siteId,
      }),
    onSuccess: (device) => {
      onCreated(device);
    },
    onError: (error) => {
      console.error('Error creating device:', error);
    },
  });

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    createMutation.mutate();
  };

  return (
    <Modal onClose={onClose} style={{ maxWidth: 500 }}>
      <h3 className="text-center mb-30">{t('device.form.create', { type: typeTranslation })}</h3>
      <FormPanel>
        <form onSubmit={handleSubmit}>
          <div className={styles.DeviceInfo}>
            <img className={styles.DeviceImg} src={`/assets/${archivedDevice.deviceType.toLowerCase()}@2x.png`} />
            <div className={styles.DeviceDetails}>
              <div className={styles.DeviceRow}>
                <span className={styles.DeviceLabel}>{t('devices.table.deviceType')}</span>
                <span>{typeTranslation}</span>
              </div>
              {archivedDevice.macAddress && (
                <div className={styles.DeviceRow}>
                  <span className={styles.DeviceLabel}>{t('devices.table.macAddress')}</span>
                  <span>{archivedDevice.macAddress}</span>
                </div>
              )}
              {archivedDevice.imeiNumber && (
                <div className={styles.DeviceRow}>
                  <span className={styles.DeviceLabel}>{t('devices.table.imeiNumber')}</span>
                  <span>{archivedDevice.imeiNumber}</span>
                </div>
              )}
            </div>
          </div>

          <Input label={t('beamSettings.form.description')} value={description} onValueChange={setDescription} />

          <div className="d-flex justify-content-end mt-30">
            <SubmitButton type="submit" loading={createMutation.isPending} disabled={!description.length}>
              {t('device.form.confirm')}
            </SubmitButton>
          </div>
        </form>
      </FormPanel>
    </Modal>
  );
};
